import { splitInlineExpression } from "@/components/input/utils/splitInlineExpression";
import { jumpToNearbySiblingNode } from "@/components/tiptap/keyboard-handler/KeyboardHandler";
import { insertFileAttachment } from "@/components/tiptap/utils/insertFileAttachment";
import { insertImage } from "@/components/tiptap/utils/insertImage";
import { documentScope } from "@/components/tiptap/utils/uploadScope";
import { deleteNodes, pasteNodes } from "@/input/utils/editOperators";
import { cursorIsActive } from "@/models/CursorModel";
import { InlineNodeModel } from "@/models/nodes/InlineNodeModel";
import { VALID_IMAGE_TYPES, validateImageFile } from "@/utils/validateImageFile";

import type { PluginState } from "./copyPasteKey";
import type { UploadScope } from "@/components/tiptap/utils/uploadScope";
import type { DocumentModel } from "@/models/DocumentModel";
import type { NodeModel } from "@/models/nodes/types";
import type { EditorView } from "@tiptap/pm/view";
/**
 * Final stage of pasting content
 * inserts pasted nodes into Document
 * prevents default ProseMirror pasting if it is a "partial" data
 * (i.e. math nodes inside math block)
 */
export function hydratePastedContent(
  pluginState: PluginState | undefined,
  doc: DocumentModel,
  clipboardData: ClipboardEvent["clipboardData"],
  view: EditorView,
  _status: "pasted" | "moved" | "duplicated",
  uploadScope?: UploadScope,
) {
  return doc.transact(() => {
    const parsedNodes = pluginState?.parsedNodes ?? [];

    if (pluginState) {
      pluginState.parsedNodes = null;
    }

    const { tiptap } = doc.editor;

    if (!tiptap) {
      return false;
    }

    const file = clipboardData?.files[0];

    if (doc.id && file) {
      const isImage = VALID_IMAGE_TYPES.includes(file.type);

      const scope = uploadScope ?? documentScope(doc.id);

      if (isImage && validateImageFile(file)) {
        insertImage(view, view.state.selection.head, file, scope);
        return true;
      }

      if (!isImage && insertFileAttachment(view, view.state.selection.head, file, scope)) {
        return true;
      }
    }

    const isOutsideExpression = !doc.cursor.isActive;

    // "Partial" content is bare inner math nodes (to be merged into the current expression),
    // as opposed to top-level wrapper/graph nodes. Derived from the pasted node shape.
    const firstNode = parsedNodes[0]?.nodes[0];
    const isPartial = !!firstNode && firstNode.type !== "wrapper" && firstNode.type !== "graph";
    let newInlineNode: InlineNodeModel | undefined;

    const { selectedNodes } = doc.selection;

    if (parsedNodes.length || !isOutsideExpression) {
      if (selectedNodes.length) {
        deleteNodes(selectedNodes);
        doc.selection.resetSelection();
      }

      if (isPartial && isOutsideExpression) {
        newInlineNode = doc.create(InlineNodeModel);
        tiptap.chain().insertInlineExpression(newInlineNode).run();
      }

      let splitCursor: null | number = null;

      const splitExpression = !isPartial && !isOutsideExpression;
      if (splitExpression) {
        splitCursor = splitInlineExpression(doc);
        tiptap.commands.focus(tiptap.state.selection.anchor);
      }

      doc.batchCreateNodes(() => {
        const pastedNodes: NodeModel[] = [];
        for (const nodesJson of parsedNodes) {
          pastedNodes.push(
            ...pasteNodes(
              doc,
              nodesJson.nodes,
              { skipRootIdReset: !isPartial, ignoreActiveCursor: splitExpression },
              newInlineNode,
            ),
          );
        }
        return pastedNodes;
      });

      // We need this to force update the tiptap editor
      if (isPartial && isOutsideExpression) {
        view.dispatch(view.state.tr);
      }

      if (splitCursor) {
        // delete space added by splitInlineExpression
        // ideally space shouldn't be added in the first place, but the node merging logic requires it
        // TODO: if we do it in one transaction (splitInlineExpression + CopyPaste), then node merging
        // shouldn't be an issue and we can split the expression without adding space
        queueMicrotask(() => {
          if (splitCursor) {
            tiptap
              .chain()
              .deleteRange({ from: splitCursor, to: splitCursor + 1 })
              .run();
          }
        });
      }

      if (!cursorIsActive(doc.cursor)) {
        queueMicrotask(() => {
          jumpToNearbySiblingNode(doc);
        });
      }
      // FIXME: copy-paste de-sync on large pasted content
      // @see https://linear.app/corca-research/issue/D-1617/copy-paste-desync-on-large-pasted-content
    }

    return isPartial;
  }, "CopyPaste:paste");
}
